import React, { useState } from 'react';
import styled from 'styled-components';
import { useWorks } from '../../contexts/WorksContext';
import { Work, WorkCategory } from '../../models/Work';

const AdminContainer = styled.div`
  padding: 20px;
  max-width: 900px;
  margin: 0 auto;
  background-color: #fafafa;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const AdminHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
`;

const AdminTitle = styled.h2`
  font-family: 'Lucida Bright', 'Lucida Serif', Georgia, serif;
  color: #333;
  margin: 0;
`;

const Button = styled.button`
  background-color: #333;
  color: white;
  border: none;
  padding: 10px 18px;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  border-radius: 4px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #444;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const FormContainer = styled.div`
  background-color: white;
  padding: 30px;
  margin-bottom: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const FormTitle = styled.h3`
  margin-top: 0;
  font-family: 'Lucida Bright', 'Lucida Serif', Georgia, serif;
  color: #333;
  margin-bottom: 20px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  color: #444;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  font-family: 'Inter', sans-serif;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.95rem;
  
  &:focus {
    outline: none;
    border-color: #666;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.05);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  font-family: 'Inter', sans-serif;
  border: 1px solid #ddd;
  border-radius: 4px;
  min-height: 120px;
  font-size: 0.95rem;
  
  &:focus {
    outline: none;
    border-color: #666;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.05);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  font-family: 'Inter', sans-serif;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  font-size: 0.95rem;
  
  &:focus {
    outline: none;
    border-color: #666;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.05);
  }
`;

const FormFullWidth = styled.div`
  grid-column: 1 / -1;
`;

const WorksList = styled.div`
  margin-top: 30px;
`;

const WorksListHeader = styled.h3`
  font-family: 'Lucida Bright', 'Lucida Serif', Georgia, serif;
  color: #333;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
`;

const WorkItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  margin-bottom: 10px;
  border-radius: 6px;
  background-color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
`;

const WorkInfo = styled.div`
  flex: 1;
`;

const WorkTitle = styled.h4`
  margin: 0 0 5px 0;
  font-family: 'Lucida Bright', 'Lucida Serif', Georgia, serif;
  color: #333;
`;

const WorkMeta = styled.div`
  font-size: 0.9rem;
  color: #666;
  font-family: 'Inter', sans-serif;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const EditButton = styled(Button)`
  background-color: #4a6fb5;
  padding: 8px 15px;
  font-size: 0.9rem;
  
  &:hover {
    background-color: #385692;
  }
`;

const DeleteButton = styled(Button)`
  background-color: #d9534f;
  padding: 8px 15px;
  font-size: 0.9rem;
  
  &:hover {
    background-color: #b84742;
  }
`;

const EmptyMessage = styled.p`
  text-align: center;
  padding: 30px;
  color: #888;
  background-color: white;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
  font-family: 'Inter', sans-serif;
`;

interface WorkFormData {
  title: string;
  year: string;
  generalInstrumentation: string;
  detailedInstrumentation: string;
  duration: string;
  category: WorkCategory;
  ensembleType: string;
  premiere: string;
  commissioner: string;
  description: string;
  scoreUrl: string;
  videoUrl: string;
  soundcloudUrl: string;
}

const initialFormData: WorkFormData = {
  title: '',
  year: new Date().getFullYear().toString(),
  generalInstrumentation: '',
  detailedInstrumentation: '',
  duration: '',
  category: WorkCategory.CHORAL,
  ensembleType: '',
  premiere: '',
  commissioner: '',
  description: '',
  scoreUrl: '',
  videoUrl: '',
  soundcloudUrl: ''
};

const WorksAdmin: React.FC = () => {
  const { works, addWork, updateWork, deleteWork } = useWorks();
  const [showForm, setShowForm] = useState(false);
  const [editingWorkId, setEditingWorkId] = useState<string | null>(null);
  const [formData, setFormData] = useState<WorkFormData>(initialFormData);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const workData = {
      ...formData,
      year: parseInt(formData.year)
    };
    
    if (editingWorkId) {
      updateWork(editingWorkId, workData);
    } else {
      addWork(workData);
    }
    
    resetForm();
  };

  const handleEdit = (work: Work) => {
    setFormData({
      title: work.title,
      year: work.year.toString(),
      generalInstrumentation: work.generalInstrumentation,
      detailedInstrumentation: work.detailedInstrumentation || '',
      duration: work.duration || '',
      category: work.category,
      ensembleType: work.ensembleType || '',
      premiere: work.premiere || '',
      commissioner: work.commissioner || '',
      description: work.description || '',
      scoreUrl: work.scoreUrl || '',
      videoUrl: work.videoUrl || '',
      soundcloudUrl: work.soundcloudUrl || ''
    });
    setEditingWorkId(work.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this work?')) {
      deleteWork(id);
    }
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setEditingWorkId(null);
    setShowForm(false);
  };

  // Sort works by year (newest first)
  const sortedWorks = [...works].sort((a, b) => b.year - a.year);

  return (
    <AdminContainer>
      <AdminHeader>
        <AdminTitle>Works Administration</AdminTitle>
        <Button onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : 'Add New Work'}
        </Button>
      </AdminHeader>
      
      {showForm && (
        <FormContainer>
          <FormTitle>{editingWorkId ? 'Edit Work' : 'Add New Work'}</FormTitle>
          <form onSubmit={handleSubmit}>
            <FormGrid>
              <FormGroup>
                <Label htmlFor="title">Title *</Label>
                <Input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="year">Year *</Label>
                <Input
                  type="number"
                  id="year"
                  name="year"
                  value={formData.year}
                  onChange={handleInputChange}
                  required
                  min="1900"
                  max={new Date().getFullYear() + 10}
                />
              </FormGroup>
              
              <FormFullWidth>
                <FormGroup>
                  <Label htmlFor="description">Description</Label>
                  <TextArea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </FormFullWidth>
              
              <FormGroup>
                <Label htmlFor="generalInstrumentation">General Instrumentation *</Label>
                <Input
                  type="text"
                  id="generalInstrumentation"
                  name="generalInstrumentation"
                  value={formData.generalInstrumentation}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="detailedInstrumentation">Detailed Instrumentation</Label>
                <Input
                  type="text"
                  id="detailedInstrumentation"
                  name="detailedInstrumentation"
                  value={formData.detailedInstrumentation}
                  onChange={handleInputChange}
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="duration">Duration</Label>
                <Input
                  type="text"
                  id="duration"
                  name="duration"
                  value={formData.duration}
                  onChange={handleInputChange}
                  placeholder="e.g. 5"
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="category">Category *</Label>
                <Select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  {Object.values(WorkCategory).map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="ensembleType">Ensemble Type</Label>
                <Input
                  type="text"
                  id="ensembleType"
                  name="ensembleType"
                  value={formData.ensembleType}
                  onChange={handleInputChange}
                  placeholder="e.g. A cappella, Solo, With ensemble"
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="premiere">Premiere</Label>
                <Input
                  type="text"
                  id="premiere"
                  name="premiere"
                  value={formData.premiere}
                  onChange={handleInputChange}
                  placeholder="Venue, date"
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="commissioner">Commissioner</Label>
                <Input
                  type="text"
                  id="commissioner"
                  name="commissioner"
                  value={formData.commissioner}
                  onChange={handleInputChange}
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="scoreUrl">Score URL</Label>
                <Input
                  type="text"
                  id="scoreUrl"
                  name="scoreUrl"
                  value={formData.scoreUrl}
                  onChange={handleInputChange}
                  placeholder="https://issuu.com/..."
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="videoUrl">Video URL</Label>
                <Input
                  type="text"
                  id="videoUrl"
                  name="videoUrl"
                  value={formData.videoUrl}
                  onChange={handleInputChange}
                  placeholder="https://youtube.com/..."
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="soundcloudUrl">Soundcloud URL</Label>
                <Input
                  type="text"
                  id="soundcloudUrl"
                  name="soundcloudUrl"
                  value={formData.soundcloudUrl}
                  onChange={handleInputChange}
                  placeholder="https://soundcloud.com/..."
                />
              </FormGroup>
            </FormGrid>
            
            <Button type="submit">
              {editingWorkId ? 'Update Work' : 'Save Work'}
            </Button>
          </form>
        </FormContainer>
      )}
      
      <WorksList>
        <WorksListHeader>Existing Works</WorksListHeader>
        {sortedWorks.length === 0 ? (
          <EmptyMessage>No works found. Add some works using the form above.</EmptyMessage>
        ) : (
          sortedWorks.map(work => (
            <WorkItem key={work.id}>
              <WorkInfo>
                <WorkTitle>{work.title}</WorkTitle>
                <WorkMeta>
                  {work.year} • {work.category} • {work.ensembleType || work.generalInstrumentation} • {work.duration || 'Duration N/A'}
                </WorkMeta>
              </WorkInfo>
              <ActionButtons>
                <EditButton onClick={() => handleEdit(work)}>Edit</EditButton>
                <DeleteButton onClick={() => handleDelete(work.id)}>Delete</DeleteButton>
              </ActionButtons>
            </WorkItem>
          ))
        )}
      </WorksList>
    </AdminContainer>
  );
};

export default WorksAdmin; 